/**
 * fileMeta — the sentence a document row prints about itself.
 *
 * `filename · size · Open` IS THE WHOLE ANATOMY of a document slot, and every part of it is
 * derived: the name from a storage URL nobody should read raw, the size from a byte count, the
 * refusal from a limit. Deriving it here rather than in the row means the same document reads the
 * same in the driver file, on a roadworthiness certificate and in a service record — and means
 * the one part that can refuse a file is provable rather than clicked at.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

/**
 * `1.2 MB` · `840 KB` · `312 B`. Null when there is no size to print.
 *
 * ONE DECIMAL, AND KB BELOW A MEGABYTE. `0.8 MB` is a worse answer than `840 KB` for a scan, and
 * `1258291 bytes` is not an answer at all.
 */
export function formatFileSize(bytes: number | null | undefined): string | null {
    if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return null;
    if (bytes < 1024) return `${Math.round(bytes)} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
    const mb = kb / 1024;
    return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/**
 * The filename inside a stored document's URL.
 *
 * THE ROW SHOWS THE FILE, NOT THE PATH. Storage URLs carry a bucket, a contract folder and a
 * percent-encoded name; what an admin recognises is the last segment, decoded, without the query
 * string a signed URL drags along.
 */
export function fileNameFromUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    const withoutQuery = url.split('?')[0].split('#')[0];
    const segment = withoutQuery.split('/').filter(Boolean).pop();
    if (!segment) return null;
    try {
        return decodeURIComponent(segment);
    } catch {
        // A malformed escape is not a reason to show nothing.
        return segment;
    }
}

/**
 * The refusal for a file that is too big, or null when it is not.
 *
 * IT NAMES THE FILE, ITS SIZE AND THE LIMIT — the three things needed to decide what to do next,
 * in the same verbatim-under-the-field slot a server refusal lands in.
 */
export function oversizeMessage(
    file: { name: string; size: number },
    maxSizeMB: number,
): string | null {
    const limit = maxSizeMB * 1024 * 1024;
    if (file.size <= limit) return null;
    return `${file.name} is ${formatFileSize(file.size)} — the limit is ${maxSizeMB} MB.`;
}

/**
 * The meta line for a document slot at rest: `ghana-card-front.jpg · 1.2 MB`, or what an empty
 * slot says. Records are never urgent — an absent document reads as absent, not as a task.
 */
export function slotMeta(
    name: string | null,
    size: number | null | undefined,
    empty = 'Not on file',
): string {
    if (!name) return empty;
    const printed = formatFileSize(size);
    return printed ? `${name} · ${printed}` : name;
}
